import {
  buildScatterPlotElement,
  compileScatterPlotSpec,
  validateScatterPlotSpec,
} from "@/lib/ai/visualization/views/scatter-plot/logic";
import { scatterPlotAuthoringSchema } from "@/lib/ai/visualization/views/scatter-plot/schema";
import { defineVizView } from "@/lib/ai/visualization/views/shared/view-definition";

/**
 * `scatter-plot` definition の SSOT エントリ。
 */
export const scatterPlotDefinition = defineVizView({
  type: "scatter-plot",
  componentType: "ScatterPlot",
  authoringSchema: scatterPlotAuthoringSchema,
  authoringPrompt: {
    summary:
      "Relationship chart for two quantitative axes, optionally grouped or sized by a third field.",
    requiredKeys: ["xKey", "xLabel", "yKey", "yLabel"],
    optionalKeys: ["zKey", "seriesByKey"],
    selectionRules: [
      "xKey and yKey must be different metric keys.",
      "Always provide xLabel and yLabel in task.outputLanguage.",
      "When zKey is set, it must be different from xKey and yKey.",
      "When a third quantitative measure helps explain overlap, importance, or magnitude, set zKey so point size encodes that measure.",
      "Choose scatter only when multiple comparable rows exist and both axes are quantitative measures.",
      "Prefer datasets that preserve one point per observed row. When grouping or color helps, keep group identity in seriesByKey instead of collapsing each group into one summary point.",
      "Avoid summary datasets where each point would only represent one aggregate category bucket unless the instruction explicitly asks to compare summary points.",
      "Do not use scatter for time-order stories or current-versus-previous comparisons that need explicit sequencing.",
    ],
    avoidWhen: [
      "Simple time trends, rankings, or detailed audit tables.",
      "Requests that have only one meaningful metric.",
      "Very small samples that are easier to read as bars or a table.",
      "Period-compare narratives that need explicit current and previous pairing.",
    ],
    minimalExamples: [
      {
        label: "two-metric-relationship",
        view: {
          type: "scatter-plot",
          xKey: "clicks",
          xLabel: "Clicks",
          yKey: "ctr",
          yLabel: "CTR",
        },
      },
      {
        label: "bubble-relationship",
        view: {
          type: "scatter-plot",
          xKey: "impressions",
          xLabel: "Impressions",
          yKey: "clicks",
          yLabel: "Clicks",
          zKey: "ctr",
        },
      },
    ],
  },
  datasetRequirements: {
    requiresTimeDimension: false,
    requiresCategoryDimension: false,
    minMetricCount: 2,
    requiresCompareMode: false,
  },
  validateSpec: validateScatterPlotSpec,
  buildElement: buildScatterPlotElement,
  compileSpec: compileScatterPlotSpec,
});
